import { BASE_VISIT_FORM } from "./visitForms";
import {
  DEFAULT_PLAN_ACTION_EXAMPLE,
  DEFAULT_PLAN_LEVEL_HEADING,
  DEFAULT_PLAN_SUPPORT,
  DEFAULT_RETENTION_MATCHING_RECORDS,
  type PerformanceConfig,
} from "./schema";

/**
 * Neutral vocabulary and the fixed structures the client forms describe (analysis §A.4, §A.5).
 * Verticals override labels, thresholds and the coaching-type list; the forms' shape is code.
 */
export const BASE_PACK: PerformanceConfig = {
  version: 1,
  vertical: "generic",
  org: { name: null, shortName: null, hris: null },
  roles: { employee: "Employee", manager: "Manager", hr: "HR" },
  levels: {
    staff: "Team member",
    dept: "Department head",
    community: "Location leader",
    regional: "Regional leader",
    corporate: "Corporate",
  },
  terms: {
    coaching: "Coaching",
    pip: "Performance Improvement Plan",
    pipShort: "PIP",
    eval: "Annual evaluation",
    visit: "Site visit",
    site: "Location",
  },
  visits: { runMinLevel: "regional", forms: [BASE_VISIT_FORM] },
  evaluations: {
    flagBelowPct: 50,
    scale: [
      { n: 1, name: "Below expectations", sub: "Needs improvement" },
      { n: 2, name: "Approaching expectations", sub: "Room for improvement" },
      { n: 3, name: "Meeting expectations", sub: "Proficient" },
      { n: 4, name: "Exceeds expectations", sub: "" },
    ],
    sections: [
      {
        key: "s1",
        name: "Understanding of job expectations",
        questions: [
          "Does the employee understand what the expectations of this role are and what is expected of them to be successful?",
          "Does the employee understand and fulfil the job responsibilities in a timely manner, meeting all deadlines?",
          "Does the employee appropriately take initiative, prioritise responsibilities and apply urgency when necessary?",
          "Does the employee abide by company policies and procedures?",
          "Does the employee accept direction and execute each directive timely and effectively?",
          "Are training and compliance requirements being met?",
        ],
        kpi: false,
        dev: false,
      },
      {
        key: "s2",
        name: "Communication",
        questions: [
          "Does the employee communicate professionally and effectively with all employees, residents, families and third parties?",
          "Is the employee professional and respectful in all communications, written and verbal?",
          "Does the employee communicate all pertinent details to the appropriate person within a reasonable timeframe?",
          "Does the employee communicate effectively interdepartmentally?",
          "If applicable, do direct reports feel supported and comfortable raising concerns?",
        ],
        kpi: false,
        dev: false,
      },
      {
        key: "s3",
        name: "Key performance indicators",
        questions: [],
        kpi: true,
        dev: false,
      },
      {
        key: "s4",
        name: "Core company competency",
        questions: [
          "Does the employee exercise actions that align with the mission and vision?",
          "Does the employee focus on the development of direct reports and collaborate effectively?",
          "Does the employee lead by example, representing the company positively and professionally?",
        ],
        kpi: false,
        dev: false,
      },
      {
        key: "s5",
        name: "Developmental opportunities",
        questions: [],
        kpi: false,
        dev: true,
      },
    ],
    kpiCounts: { year1: 3, year2: 4 },
    maxScores: { year1: 68, year2: 72 },
    minDevelopment: 3,
  },
  plans: {
    activeMonths: 12,
    reminderDays: [1, 3, 7, 14],
    maxActions: 5,
    maxReviews: 4,
    levels: [
      { key: "first", name: "First Counseling", letter: false },
      { key: "written", name: "Written Counseling", letter: true },
      { key: "final", name: "Final Written Counseling", letter: true },
      { key: "termination", name: "Termination", letter: true },
    ],
    // Keys are stable; `freeText` marks the entry that asks for a description (never its label).
    offenceTypes: [
      { key: "attendance", name: "Attendance Issues", freeText: false },
      { key: "policy", name: "Violation of Company Policy", freeText: false },
      { key: "substandard_work", name: "Substandard Work", freeText: false },
      { key: "safety", name: "Safety Violation", freeText: false },
      { key: "professionalism", name: "Lack of Professionalism", freeText: false },
      { key: "other", name: "Other", freeText: true },
    ],
    // E3: two levels up the starter's line, then HR (O-2); reminders 7 days ahead (D15).
    approvalLevels: 2,
    support: [...DEFAULT_PLAN_SUPPORT],
    remindDaysDefault: 7,
    levelHeading: DEFAULT_PLAN_LEVEL_HEADING,
    actionExample: DEFAULT_PLAN_ACTION_EXAMPLE,
  },
  coachingTypes: [
    {
      key: "recognition",
      name: "Recognition",
      description: "Positive documentation. Says what they did and why it mattered.",
      multi: false,
      form: null,
      minLevel: null,
      titleHint: "Helped a colleague meet a deadline",
      prompt: "Say what they did and why it mattered.",
      tone: "good",
      allowDisagree: true,
      retired: false,
    },
    {
      key: "coaching_discussion",
      name: "Coaching discussion",
      description: "A conversation about performance or behaviour.",
      multi: false,
      form: null,
      minLevel: null,
      titleHint: "Timekeeping and breaks",
      prompt: "What was discussed, what was agreed, and anything they raised.",
      tone: "neutral",
      allowDisagree: true,
      retired: false,
    },
    {
      key: "policy_procedure",
      name: "Policy and procedure",
      description: "Resetting an expectation, or covering a policy.",
      multi: false,
      form: null,
      minLevel: null,
      titleHint: "Clocking-in procedure",
      prompt: "What was discussed, what was agreed, and anything they raised.",
      tone: "neutral",
      allowDisagree: true,
      retired: false,
    },
    {
      key: "team_meeting",
      name: "Team meeting",
      description: "One entry filed to everyone who attended. Attach the agenda.",
      multi: true,
      form: null,
      minLevel: null,
      titleHint: "Weekly team meeting: priorities",
      prompt: "What was discussed, what was agreed, and anything they raised.",
      tone: "neutral",
      allowDisagree: true,
      retired: false,
    },
    {
      key: "annual_evaluation",
      name: "Annual evaluation",
      description: "Opens the evaluation form.",
      multi: false,
      form: "eval",
      minLevel: null,
      titleHint: "",
      prompt: "",
      tone: "neutral",
      allowDisagree: true,
      retired: false,
    },
    {
      key: "site_visit",
      name: "Site visit form",
      description: "The location walk-through. Regional and up.",
      multi: false,
      form: "visit",
      minLevel: "regional",
      titleHint: "",
      prompt: "",
      tone: "neutral",
      allowDisagree: true,
      retired: false,
    },
  ],
  // Rated 1 to 5 (operator ruling 2026-10-12); a 1 or a 5 needs a reason. HR renames each one.
  visitScale: [
    { n: 5, label: "Exceeds standards" },
    { n: 4, label: "Meets standards" },
    { n: 3, label: "Mostly meets standards" },
    { n: 2, label: "Improving, not yet meeting standards" },
    { n: 1, label: "Needs immediate attention" },
  ],
  syncFields: [
    "Reporting line",
    "Job title",
    "Location",
    "Department",
    "Start date",
    "Employment status",
  ],
  writeBack: [
    { what: "Recognition", enabled: false },
    { what: "Coaching discussion", enabled: false },
    { what: "Policy and procedure", enabled: false },
    { what: "Team meeting", enabled: false },
    { what: "Annual evaluation", enabled: true },
    { what: "PIP, any level", enabled: true },
    { what: "Termination detail", enabled: true },
    { what: "Site visit", enabled: false },
  ],
  // Never perf_hr (the schema refuses it): the performance administrator role is granted only by
  // HR mapping a position by hand, an override or provisioning, never by a title matching a glob.
  defaultPositionMappings: [],
  // E9: still to be confirmed with each customer; stored only until Records (E8) purges.
  retention: { period: "unset", matchingRecords: DEFAULT_RETENTION_MATCHING_RECORDS },
  // Spec 2026-10-19 §1: everyone is live until HR picks locations. Every pack keeps this.
  rollout: { liveLocations: null },
};
